import React, { useContext, useState } from "react";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffectOnce } from "react-use";
import {
  CountryType,
  FoundersType,
  ResultContext,
  ShareholdersType,
  vestedShares,
} from "../ResultContext";
import { Results, Slide2Type, SlideName } from "../constants/Questions";
import { colorPairs, lightBrown } from "../styles/PDFColors";
Chart.register(ChartDataLabels);

export function GraphMaker({ setImage }: { setImage: (img: string) => void }) {
  const [done, setDone] = useState(false);
  const { result } = useContext(ResultContext);

  const sortedResults = Results.sort((a, b) => a.position - b.position);
  const labels: string[] = [];
  const data: number[] = [];
  const sharesPerFounderPercentage: number[] = [];
  let externalShares = "0";
  let externalSharesPercentages = 0;
  const backgroundColors: string[] = [];

  sortedResults.map((item) => {
    if (item.resultType === SlideName.Slide2) {
      const typedItem = item as Slide2Type;
      const sharesResult = result[
        typedItem.sharesQuestionId
      ] as ShareholdersType;

      const vestingSharesResult = result[
        typedItem.vestingSharesId
      ] as vestedShares;

      const foundersResult = result[typedItem.foundersQuestionId] as {
        name: string;
        country: CountryType;
        type: FoundersType;
      }[];

      if (!sharesResult || !vestingSharesResult || !foundersResult) {
        return "";
      }

      if (sharesResult.type === "Percentage") {
        sharesResult.shares.map((s) =>
          sharesPerFounderPercentage.push(parseFloat(s.share))
        );
        externalShares = sharesResult.externalShareholders.external
          ? sharesResult.externalShareholders.share
          : "0";
      } else {
        externalShares = sharesResult.externalShareholders.external
          ? sharesResult.externalShareholders.share
          : "0";
        const sharesPerFounder = sharesResult.shares.map((s) =>
          parseFloat(s.share)
        );

        let sum = sharesPerFounder.reduce((sum, current) => sum + current, 0);

        sum += parseFloat(externalShares);
        sharesPerFounder.map((s) =>
          sharesPerFounderPercentage.push(
            parseFloat(((s / sum) * 100).toFixed(2))
          )
        );

        externalSharesPercentages = (parseFloat(externalShares) / sum) * 100;
      }

      sharesPerFounderPercentage.map((share, index) => {
        if (vestingSharesResult[index].vestedOptions === "all") {
          data.push(share);
          labels.push(`${foundersResult[index].name} not earned shares`);
          backgroundColors.push(colorPairs[index * 2]);
        } else if (
          vestingSharesResult[index].vestedShares !== "0" &&
          vestingSharesResult[index].vestedShares !== "100"
        ) {
          data.push(
            (share * parseFloat(vestingSharesResult[index].vestedShares)) / 100
          );
          labels.push(`${foundersResult[index].name} not earned shares`);
          backgroundColors.push(colorPairs[index * 2]);
          data.push(
            share *
              (1 - parseFloat(vestingSharesResult[index].vestedShares) / 100)
          );
          labels.push(`${foundersResult[index].name} earned shares`);
          backgroundColors.push(colorPairs[index * 2 + 1]);
        } else if (vestingSharesResult[index].vestedShares === "0") {
          data.push(
            share *
              (1 - parseFloat(vestingSharesResult[index].vestedShares) / 100)
          );
          labels.push(`${foundersResult[index].name} earned shares`);
          backgroundColors.push(colorPairs[index * 2 + 1]);
        } else {
          data.push(
            (share * parseFloat(vestingSharesResult[index].vestedShares)) / 100
          );
          labels.push(`${foundersResult[index].name} not earned shares`);
          backgroundColors.push(colorPairs[index * 2]);
        }
      });

      if (sharesResult.externalShareholders.external) {
        if (sharesResult.type === "Percentage") {
          data.push(parseFloat(sharesResult.externalShareholders.share));
          labels.push("Other Shareholders");
          backgroundColors.push(lightBrown);
        } else {
          data.push(externalSharesPercentages);
          labels.push("Other Shareholders");
          backgroundColors.push(lightBrown);
        }
      }
    }
  });

  useEffectOnce(() => {
    const canvas = document.createElement("canvas");

    const progressChart = new Chart(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      canvas,
      {
        type: "pie",
        data: {
          labels: labels,
          datasets: [
            {
              data: data,
              backgroundColor: backgroundColors,
            },
          ],
        },
        options: {
          plugins: {
            datalabels: {
              color: "black",
              anchor: "end",
              align: "start",
              offset: 30,
              font: {
                size: 60,
              },
              formatter: function (value) {
                return Math.round(value) + "%";
              },
            },
            legend: {
              position: "bottom",
              labels: {
                boxWidth: 65,
                font: { size: 42 },
                textAlign: "left",
                padding: 13,
              },
            },
          },
          animation: {
            duration: 0,
            onComplete: () => {
              setImage(progressChart.toBase64Image());
              setDone(true);
            },
          },
        },
      }
    );

    const main = document.getElementById("chart");
    if (main) {
      main.appendChild(canvas);
    }
  });

  return <div style={{ display: done ? "none " : "block" }} id="chart"></div>;
}
